import { ComponentFeature } from './internal';
import { AwsUi } from '../../../core/runtime/src';
import { addClass, removeClass } from '../../../core/utils/src/dom';
import layoutDefaults from '../../app-layout/src/layout/defaults';
// get default minimum content width
var minTableWidth = layoutDefaults.get({}).minContentWidth;
export var StickyState;
(function (StickyState) {
    StickyState[StickyState["DEFAULT"] = 0] = "DEFAULT";
    StickyState[StickyState["STICKY"] = 1] = "STICKY";
    StickyState[StickyState["LEAVING"] = 2] = "LEAVING";
})(StickyState || (StickyState = {}));
var StickyHandler = /** @class */ (function () {
    function StickyHandler(node, verticalOffset) {
        this.node = node;
        this.verticalOffset = verticalOffset;
        this.enabled = false;
        this.stickyState = StickyState.DEFAULT;
        this.verticalScrollListener = this.verticalScrollListener.bind(this);
        this.scrollToTop = this.scrollToTop.bind(this);
        this.scrollUpBy = this.scrollUpBy.bind(this);
        this.addItemsFocusListener = this.addItemsFocusListener.bind(this);
        this.removeItemsFocusListener = this.removeItemsFocusListener.bind(this);
        this.itemsFocusHandler = this.itemsFocusHandler.bind(this);
        this.handleContainerChange = this.handleContainerChange.bind(this);
    }
    StickyHandler.prototype.shouldStick = function () {
        if (this.enabled && window.innerWidth > 768) {
            var rootElement = this.getRootElement();
            // prevent sticking when content area becomes horizontally scrollable
            return rootElement.getBoundingClientRect().width > minTableWidth;
        }
        return false;
    };
    StickyHandler.prototype.attach = function () {
        var _this = this;
        this.scrollParent = this.getScrollParent(this.node);
        (this.scrollParent || window).addEventListener('scroll', this.verticalScrollListener);
        this.enabled = true;
        // this function is called before rendering, needs to wait until sticky elements will be rendered
        setTimeout(function () { return _this.verticalScrollListener(); }, 0);
        AwsUi.stickyElements.register(this.node, this.handleContainerChange);
    };
    StickyHandler.prototype.detach = function () {
        (this.scrollParent || window).removeEventListener('scroll', this.verticalScrollListener);
        this.setState(StickyState.DEFAULT);
        this.enabled = false;
        AwsUi.stickyElements.unregister(this.node, this.handleContainerChange);
    };
    //update sticky layout manually
    //should be invoked when the container's width changes
    StickyHandler.prototype.handleContainerChange = function () {
        if (this.enabled) {
            this.updateLayout();
        }
    };
    StickyHandler.prototype.updateLayout = function () {
        var newState = this.getStickyPosition();
        var currentState = this.getCurrentStickyState();
        var container = this.getStickyNode();
        var headerStrut = this.getHeaderStrut();
        var strutOriginal = this.getStrutOriginal();
        if (container && headerStrut && strutOriginal) {
            if (newState !== StickyState.DEFAULT) {
                var containerWidth = headerStrut.offsetWidth || strutOriginal.offsetWidth;
                var strutHeight = strutOriginal.offsetHeight;
                headerStrut.style.height = strutHeight + "px";
                container.style.width = containerWidth + "px";
            }
            if (newState === StickyState.STICKY) {
                var rootElement = this.getRootElement();
                var left = rootElement.getBoundingClientRect().left;
                container.style.top = this.getParentOffsetTop() + "px";
                container.style.left = left + "px";
            }
            else {
                container.style.top = null;
                container.style.left = null;
            }
        }
        if (newState !== currentState) {
            this.setState(newState);
        }
    };
    StickyHandler.prototype.getCurrentStickyState = function () {
        return this.stickyState;
    };
    StickyHandler.prototype.getCurrentStickyClass = function () {
        switch (this.getCurrentStickyState()) {
            case StickyState.STICKY: {
                return this.stickyVisibleClass;
            }
            case StickyState.LEAVING: {
                return this.stickyLeavingClass;
            }
            default: {
                return '';
            }
        }
    };
    StickyHandler.prototype.scrollToTop = function () {
        var containerTop = this.node.getBoundingClientRect().top;
        var stickyTop = this.getStickyNode().getBoundingClientRect().top;
        var scrollDist = stickyTop - containerTop;
        this.scrollUpBy(scrollDist);
    };
    StickyHandler.prototype.scrollUpBy = function (pxDistance) {
        if (this.scrollParent) {
            this.scrollParent.scrollTop -= pxDistance;
        }
        else {
            window.scrollTo(window.pageXOffset, window.pageYOffset - pxDistance);
        }
    };
    StickyHandler.prototype.forceUnstickIfNeeded = function (beforeUnstick, changeSource) {
        // selection of an item should not change scroll position
        if (changeSource === ComponentFeature.SELECTION) {
            return;
        }
        if (this.getCurrentStickyState() !== StickyState.DEFAULT) {
            beforeUnstick(this.scrollToTop);
        }
    };
    StickyHandler.prototype.setVerticalOffset = function (offset) {
        this.verticalOffset = offset;
        this.handleContainerChange();
    };
    // should be overloaded if sticky element's size changes upon the state change
    StickyHandler.prototype.getStickyHeight = function () {
        return this.getStickyNode().getBoundingClientRect().height;
    };
    StickyHandler.prototype.stateChangeHandler = function (state) {
        var rootElement = this.getRootElement();
        var container = this.getStickyNode();
        var headerStrut = this.getHeaderStrut();
        removeClass(rootElement, this.stickyVisibleClass);
        removeClass(rootElement, this.stickyLeavingClass);
        if (state === StickyState.STICKY) {
            addClass(rootElement, this.stickyVisibleClass);
        }
        if (state === StickyState.LEAVING) {
            addClass(rootElement, this.stickyLeavingClass);
        }
        if (state === StickyState.DEFAULT) {
            headerStrut.style.height = null;
            container.style.width = null;
        }
    };
    StickyHandler.prototype.setState = function (state) {
        this.stickyState = state;
        this.stateChangeHandler(this.stickyState);
    };
    StickyHandler.prototype.verticalScrollListener = function () {
        this.updateLayout();
    };
    StickyHandler.prototype.getParentOffsetTop = function () {
        return this.verticalOffset
            ? this.verticalOffset
            : this.scrollParent
                ? this.scrollParent.getBoundingClientRect().top
                : 0;
    };
    StickyHandler.prototype.getStickyPosition = function () {
        if (!this.shouldStick()) {
            return StickyState.DEFAULT;
        }
        var containerRect = this.node.getBoundingClientRect();
        var parentOffsetTop = this.getParentOffsetTop();
        var containerBottom = containerRect.bottom;
        var containerTop = containerRect.top;
        var stickyHeight = this.getStickyHeight();
        if (containerTop < parentOffsetTop) {
            if (parentOffsetTop + stickyHeight > containerBottom) {
                return StickyState.LEAVING;
            }
            return StickyState.STICKY;
        }
        return StickyState.DEFAULT;
    };
    // Based on Jquery UI utility function
    // https://github.com/jquery/jquery-ui/blob/master/ui/scroll-parent.js
    StickyHandler.prototype.getScrollParent = function (element) {
        var overflowRegex = /(auto|scroll)/;
        var parent = element.parentElement;
        while (parent && !overflowRegex.test(getComputedStyle(parent).overflow)) {
            parent = parent.parentElement;
        }
        return parent;
    };
    StickyHandler.prototype.itemsFocusHandler = function (event) {
        if (this.enabled) {
            var PADDING = 10;
            var headerPosition = this.getStickyNode().getBoundingClientRect();
            var targetPosition = event.target.getBoundingClientRect();
            if (targetPosition.top < headerPosition.bottom) {
                this.scrollUpBy(headerPosition.bottom - targetPosition.top + PADDING);
            }
        }
    };
    StickyHandler.prototype.addItemsFocusListener = function () {
        var tbody = this.getItemsContainer();
        if (tbody) {
            tbody.addEventListener('focus', this.itemsFocusHandler, true);
        }
    };
    StickyHandler.prototype.removeItemsFocusListener = function () {
        var tbody = this.getItemsContainer();
        if (tbody) {
            tbody.removeEventListener('focus', this.itemsFocusHandler);
        }
    };
    return StickyHandler;
}());
export { StickyHandler };
//# sourceMappingURL=base-sticky-header.js.map